import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import type { ModuleWithProgress, TrainingCertificate } from '@xsc/shared';
import { api } from '../lib/api-client.js';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/card.js';
import { Badge } from '../components/ui/badge.js';
import { Button } from '../components/ui/button.js';

/**
 * Training & Certifications — learner catalog (Task #17).
 *
 * Lists every active module for the current tenant with progress, quiz
 * status, and prerequisite gating. The catalog and prerequisite check are
 * computed against the modules array on the client; the server only
 * provides the per-(user, module) progress fields.
 */
function ProgressBar({ percent }: { percent: number }): JSX.Element {
  const clamped = Math.max(0, Math.min(100, percent));
  return (
    <div className="h-2 w-full rounded bg-surface overflow-hidden border border-border">
      <div
        className="h-full bg-accent transition-all"
        style={{ width: `${clamped}%` }}
        aria-label={`${clamped}% complete`}
      />
    </div>
  );
}

export function TrainingHomePage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const renewalStatus = searchParams.get('renewal');
  const modulesQ = useQuery({
    queryKey: ['training', 'modules'],
    queryFn: () => api.get<{ modules: ModuleWithProgress[] }>('/v1/training/modules'),
  });
  const certQ = useQuery({
    queryKey: ['training', 'certificate'],
    queryFn: () => api.get<{ certificate: TrainingCertificate | null }>('/v1/training/certificate'),
  });

  const modules = modulesQ.data?.modules ?? [];
  const completedIds = new Set(modules.filter((m) => m.is_complete).map((m) => m.id));
  const requiredTotal = modules.filter((m) => m.is_required).length;
  const requiredDone = modules.filter((m) => m.is_required && m.is_complete).length;
  const cert = certQ.data?.certificate ?? null;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Training &amp; Certifications</h1>
        <p className="text-ink-muted text-sm mt-1">
          Complete every required module to earn your certification.
        </p>
      </header>

      {/* Task #21 — terminal banners surfaced by the cert renewal-link
          server redirects when there is no module to land on (e.g.
          opted_out, expired, or "ready" with no required modules). */}
      {renewalStatus === 'opted_out' && (
        <aside role="status" className="rounded border-l-4 px-4 py-3 border-blue-300 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-700">
          <div className="font-semibold text-sm mb-1">You're unsubscribed</div>
          <p className="text-sm">
            You won't receive any more certificate renewal reminders. Your
            certificate stays valid until its expiry date.
          </p>
        </aside>
      )}
      {renewalStatus === 'expired' && (
        <aside role="status" className="rounded border-l-4 px-4 py-3 border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700">
          <div className="font-semibold text-sm mb-1">This renewal link is no longer valid</div>
          <p className="text-sm">
            Renewal links expire 60 days after issue. If your certificate
            is approaching its expiry date, an admin can resend a fresh
            reminder from the Admin → Training page.
          </p>
        </aside>
      )}
      {renewalStatus === 'ready' && (
        <aside role="status" className="rounded border-l-4 px-4 py-3 border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700">
          <div className="font-semibold text-sm mb-1">Time to renew your certificate</div>
          <p className="text-sm">
            Re-complete the required modules below and pass each quiz to
            refresh your certification for another full term.
          </p>
        </aside>
      )}

      <Card>
        <CardBody className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1">
            <div className="text-sm text-ink-muted">Required-module progress</div>
            <div className="text-lg font-medium">
              {requiredDone} / {requiredTotal} complete
            </div>
          </div>
          <div className="sm:w-1/2">
            <ProgressBar
              percent={requiredTotal > 0 ? (requiredDone / requiredTotal) * 100 : 0}
            />
          </div>
          <div>
            {cert ? (
              <Badge tone="success">Certified — {cert.certificate_ref}</Badge>
            ) : (
              <Badge tone="neutral">Not yet certified</Badge>
            )}
          </div>
        </CardBody>
      </Card>

      {modulesQ.isLoading && <p className="text-ink-muted">Loading modules…</p>}
      {modulesQ.isError && (
        <p className="text-danger">Failed to load training modules.</p>
      )}
      {!modulesQ.isLoading && modules.length === 0 && (
        <Card>
          <CardBody>
            <p className="text-ink-muted">
              No training modules are available for your account yet.
            </p>
          </CardBody>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {modules.map((m) => {
          const blockedBy = m.prerequisite_ids.filter((id) => !completedIds.has(id));
          const locked = blockedBy.length > 0;
          const blockerTitles = blockedBy
            .map((id) => modules.find((x) => x.id === id)?.title ?? id)
            .join(', ');
          return (
            <Card key={m.id}>
              <CardHeader>
                <CardTitle>{m.title}</CardTitle>
                <div className="flex items-center gap-2">
                  {m.is_required && <Badge tone="warning">Required</Badge>}
                  {m.is_complete ? (
                    <Badge tone="success">Complete</Badge>
                  ) : locked ? (
                    <Badge tone="neutral">Locked</Badge>
                  ) : (
                    <Badge tone="info">In progress</Badge>
                  )}
                </div>
              </CardHeader>
              <CardBody className="space-y-3">
                {m.description && (
                  <p className="text-sm text-ink-muted">{m.description}</p>
                )}
                <div className="flex flex-wrap items-center gap-3 text-xs text-ink-muted">
                  <span>~{m.estimated_minutes} min</span>
                  <span>·</span>
                  <span>
                    {m.completed_lessons}/{m.total_lessons} lessons
                  </span>
                  {m.has_quiz && (
                    <>
                      <span>·</span>
                      <span>
                        Quiz: {m.quiz_passed ? 'passed' : m.best_quiz_score === null ? 'not yet attempted' : `best ${m.best_quiz_score}%`}
                      </span>
                    </>
                  )}
                </div>
                <ProgressBar percent={m.lesson_progress} />
                {locked && (
                  <p className="text-xs text-ink-muted">
                    Complete first: {blockerTitles}
                  </p>
                )}
                <div className="flex justify-end">
                  {locked ? (
                    <Button variant="secondary" disabled>
                      Locked
                    </Button>
                  ) : (
                    <Link to={`/training/${m.id}`}>
                      <Button>{m.is_complete ? 'Review' : 'Open module'}</Button>
                    </Link>
                  )}
                </div>
              </CardBody>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
